/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { EuiLink, EuiSkeletonText, useEuiTheme } from "@inscopix/ideas-eui";
import { UserAvatar } from "components/UserAvatar/UserAvatar";
import { useProjectInfoQuery } from "graphql/_Types";
import moment from "moment";
import { formatDate } from "utils/formatDate";
import { isNonNullish } from "utils/isNonNullish";
import { useToolDataContext } from "pages/tool/ToolDataProvider";
import { isDefined } from "utils/isDefined";

export const ToolInfoBody = () => {
  const { tool } = useToolDataContext();
  const { euiTheme } = useEuiTheme();
  const styles = {
    dl: css`
      display: grid;
      grid-template-columns: max-content auto;
    `,
    dt: css`
      grid-column-start: 1;
      margin-right: 0.5rem;
      font-weight: bold;
      padding: 5px 0px;
    `,
    dtVertical: css`
      font-weight: bold;
      padding: 5px 0px 0px 0px;
    `,
    dd: css`
      padding: 5px 0px;
    `,
    ddVertical: css`
      padding: 0px 0px 5px 0px;
    `,
    subContent: css`
      color: ${euiTheme.colors.subduedText};
    `,
  };

  // Extract a concise domain from a URL string (e.g., "example.com").
  const getDomainFromUrl = (urlString: string): string => {
    try {
      const parsed = new URL(urlString);
      // Remove leading "www." for cleaner display
      return parsed.hostname.replace(/^www\./i, "");
    } catch {
      // Fallback: try a loose match, otherwise return the original string truncated
      const match = urlString.match(/^https?:\/\/([^/]+)/i);
      if (match && match[1] !== "") {
        return match[1].replace(/^www\./i, "");
      }
      return urlString;
    }
  };

  const toolProjectId = tool.project?.id;

  const { data, loading } = useProjectInfoQuery({
    skip: toolProjectId === undefined,
    variables: { id: toolProjectId as string },
  });

  if (loading) {
    return <EuiSkeletonText lines={3} />;
  }

  return (
    <>
      <dl css={styles.dl}>
        {isDefined(data) &&
          isNonNullish(data.project) &&
          isNonNullish(data.project.user) && (
            <>
              <dt css={styles.dt}>Owner</dt>
              <dd css={styles.dd}>
                <UserAvatar userId={data.project.user.id} size="s" />
                <br />
                <span css={styles.subContent}>
                  {data.project.user.firstName} {data.project.user.lastName}
                </span>
              </dd>
            </>
          )}
        <>
          <dt css={styles.dt}>Created</dt>
          <dd css={styles.dd}>
            {moment(tool.date_created).fromNow()}
            <br />
            <span css={styles.subContent}>{formatDate(tool.date_created)}</span>
          </dd>
        </>
        {isNonNullish(tool.docs_link) && (
          <>
            <dt css={styles.dt}>Docs</dt>
            <dd css={styles.dd}>
              <EuiLink href={tool.docs_link} target="_blank">
                {getDomainFromUrl(tool.docs_link)}
              </EuiLink>
            </dd>
          </>
        )}
        {isNonNullish(tool.code_link) && (
          <>
            <dt css={styles.dt}>Code</dt>
            <dd css={styles.dd}>
              <EuiLink href={tool.code_link} target="_blank">
                {getDomainFromUrl(tool.code_link)}
              </EuiLink>
            </dd>
          </>
        )}
      </dl>
    </>
  );
};
